import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { logoPng } from "../bridges/publicAssets";
import { LanguageMenu } from "./LanguageMenu";
import { SITE_CONTACT, SITE_DONATE, SITE_TABS } from "./navItems";
import s from "./site.module.scss";

/**
 * The header of the home page and the seven menu pages: ב״ה (at the top, as today), the logo, today's six tabs, Contact Us, Log in, Donate and the
 * compact language menu. Below 900px a menu button opens a panel with every item. `quietCreate`: the home page's quiet "Create account" link.
 */
export const SiteHeader = ({ quietCreate, blessing }: { quietCreate?: boolean; blessing?: boolean }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const first = useRef<HTMLAnchorElement>(null);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    first.current?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [open]);
  const L = (k: string) => t(`landing_nav.${k}`);
  return (
    <header className={s.header}>
      {blessing && <span className={s.bh} lang="he">ב״ה</span>}
      <Link to="/" className={s.logoLink} aria-label="ConnectYid"><img className={s.logo} src={logoPng} alt="" /></Link>
      <nav className={s.tabs} aria-label={t("landing_nav.home")}>
        {SITE_TABS.map((x) => (
          <NavLink key={x.key} to={x.path} end className={({ isActive }) => `${s.tab} ${isActive ? s.tabOn : ""}`}>{L(x.key)}</NavLink>
        ))}
      </nav>
      <div className={s.actions}>
        <Link to={SITE_CONTACT.path} className={s.textLink}>{L(SITE_CONTACT.key)}</Link>
        {quietCreate && <Link to="/registration" className={s.textLink}>{t("first_page.create_account")}</Link>}
        <Link to="/login" className={s.textLink}>{L("log_in")}</Link>
        <Link to={SITE_DONATE.path} className={s.donate}>{L(SITE_DONATE.key)}</Link>
        <LanguageMenu />
      </div>
      <button type="button" className={s.burger} aria-expanded={open} aria-controls="site-menu" aria-label={open ? t("legal_docs.close") : t("landing_nav.menu")} onClick={() => setOpen((o) => !o)}>
        {open ? <CloseRoundedIcon aria-hidden /> : <MenuRoundedIcon aria-hidden />}
      </button>
      {open && <div className={s.backdrop} role="presentation" onClick={() => setOpen(false)} />}
      <div id="site-menu" ref={panel} className={`${s.drawer} ${open ? s.drawerOpen : ""}`} hidden={!open}>
        {SITE_TABS.map((x, i) => (
          <NavLink key={x.key} to={x.path} end ref={i === 0 ? first : undefined} className={({ isActive }) => `${s.drawerItem} ${isActive ? s.drawerOn : ""}`}>{L(x.key)}</NavLink>
        ))}
        <hr className={s.rule} />
        <Link to={SITE_DONATE.path} className={s.drawerItem}>{L(SITE_DONATE.key)}</Link>
        <Link to={SITE_CONTACT.path} className={s.drawerItem}>{L(SITE_CONTACT.key)}</Link>
        <Link to="/login" className={s.drawerItem}>{L("log_in")}</Link>
        <Link to="/welcome" className={s.drawerCta}>{L("get_started")}</Link>
        <div className={s.drawerLang}><LanguageMenu /></div>
      </div>
    </header>
  );
};
